import { useEffect } from 'react'
import { useAppDispatch } from 'app/hooks'
import { useDashboardData, useDashboardWidgetSetting } from 'hooks/useDashboardState'
import { getWidgetForDashboard } from 'features/widget/asyncThunks'
import { createMediaSearch } from 'utils/runSearchUtil'
import { toFilter } from 'utils/widgetUtil'
import { getSortByOption } from 'utils/viewOptionUtil'
import { Dashboard } from 'schemas/DashboardSchema'
import MediaDomain, { MediaDomainStrings } from 'enums/MediaDomain'
import ServiceType from 'enums/ServiceType'
import { SocialWebPopularMediaWidget } from 'schemas/SocialWebPopularMediaWidgetSchema'
import useRefreshTimestamp from 'hooks/useRefreshTimestamp'

import { PopularMediaWidgetsType } from '../../types'

const createSearch = (
  widgetSetting: PopularMediaWidgetsType,
  dashboardData: Dashboard,
  isAudienceQuery?: boolean
) => {
  const filter = toFilter(widgetSetting)
  const { precision, selectedTopic, viewOption } = widgetSetting
  const { id: dashboardId, name } = dashboardData

  return createMediaSearch(
    dashboardId!,
    name!,
    [selectedTopic.topicId!],
    filter,
    precision!,
    getSortByOption(viewOption!),
    isAudienceQuery
      ? [MediaDomain.TWITTER]
      : ((widgetSetting as SocialWebPopularMediaWidget).mediaDomains! as MediaDomainStrings[]),
    100,
    isAudienceQuery ? ServiceType.AM_MEDIA : ServiceType.SW_MEDIA
  )
}

interface RunSearchEffectProps {
  widgetId: number
  children: React.ReactElement
  isAudienceQuery?: boolean
}

const RunSearchEffect = ({
  widgetId,
  children,
  isAudienceQuery
}: RunSearchEffectProps): React.ReactElement => {
  const widgetSetting = useDashboardWidgetSetting<PopularMediaWidgetsType>(widgetId)
  const dashboardData = useDashboardData()
  const dispatch = useAppDispatch()
  const refreshTimestamp = useRefreshTimestamp(widgetId)

  useEffect(() => {
    const search = createSearch(widgetSetting, dashboardData!, isAudienceQuery)

    dispatch(
      getWidgetForDashboard({
        searches: [search],
        widgetId: widgetSetting.widgetId!,
        timestamp: refreshTimestamp
      })
    )
  }, [widgetSetting, dashboardData, dispatch, isAudienceQuery, refreshTimestamp])

  return children
}

export default RunSearchEffect
